import type {
  SpeechProvider,
  SpeechProviderCallbacks,
  SpeechProviderContext,
  SpeechSession,
  SpeechStopOptions,
  SpeechError,
} from './types';
import {
  createAudioContext,
  startCapture,
  isCaptureSupported,
  CaptureError,
  type CaptureErrorCode,
  type AudioCapture,
} from './shared/audioCapture';
import {
  connectAssemblyStream,
  type AssemblyStreamHandle,
  type AssemblyTurnMessage,
} from './assemblyai/assemblyStreamClient';
import { getSttToken, SttTokenError } from './sttTokenService';
import {
  registerActiveSpeechSession,
  clearActiveSpeechSession,
  stopActiveSpeechSession,
} from './speechSessionRegistry';
import { addSentryBreadcrumb, captureException } from '@/config/sentry.config';
import { STT_CONFIG } from '@/config/appConfig';

const BEGIN_TIMEOUT_MS = STT_CONFIG.beginTimeoutMs;
const FINALIZE_TIMEOUT_MS = STT_CONFIG.finalizeTimeoutMs;
const MAX_SESSION_MS = STT_CONFIG.maxSessionMs;
const UNAVAILABLE_MESSAGE = 'Voice input is unavailable right now. Please type your question.';
const SILENCE_MESSAGE = "We didn't catch that. Try again.";

/**
 * One AssemblyAI streaming session. Owns the full state machine from the design
 * doc: idle -> requesting-permission -> connecting -> (Begin) -> listening ->
 * finalizing -> idle/error. All wiring (WS handlers, worklet callback) is set up
 * inside `start()`, in the same scope where the underlying objects are created —
 * there is no ref read from a separate effect (CLAUDE.md silent-failure pattern 1).
 */
class AssemblyAISession implements SpeechSession {
  private readonly callbacks: SpeechProviderCallbacks;
  private readonly getAccessToken: () => Promise<string>;
  private readonly registryEntry: { providerId: 'assemblyai'; stop: () => Promise<void> };

  private ctx: AudioContext | null = null;
  private capture: AudioCapture | null = null;
  private stream: AssemblyStreamHandle | null = null;
  private beginTimer: ReturnType<typeof setTimeout> | null = null;
  private finalizeTimer: ReturnType<typeof setTimeout> | null = null;
  private capTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTurnOrder: number | null = null;
  private lastCommittedText: string | null = null;
  private silentStop = false;
  private torn = false;
  private stopping = false;
  private resolveStop: (() => void) | null = null;

  constructor(callbacks: SpeechProviderCallbacks, getAccessToken: () => Promise<string>) {
    this.callbacks = callbacks;
    this.getAccessToken = getAccessToken;
    this.registryEntry = { providerId: 'assemblyai', stop: () => this.teardown() };
  }

  async start(): Promise<void> {
    // The doc's #1 mitigated risk: a remount must not leave a previous session's
    // MediaStream/WebSocket alive. Any provider's start() begins by evicting
    // whatever session (from either provider) the registry currently holds.
    await stopActiveSpeechSession();
    this.torn = false;
    this.stopping = false;

    this.callbacks.onStateChange('starting', 'Requesting microphone access…');
    // Must be synchronous relative to the user gesture: createSession()/start() is
    // called directly from MicButton's onClick, with no `await` before this line.
    // Created after an `await`, iOS gives back a `suspended` AudioContext whose
    // `resume()` never surfaces an error — audio just silently never flows.
    this.ctx = createAudioContext();

    // Mic capture (permission prompt + worklet load) and token issuance
    // (Auth0 + our backend's STT token) don't depend on each other — running
    // them concurrently instead of one after the other cuts time-to-listening
    // to roughly whichever is slower, instead of their sum.
    const capturePromise = startCapture(this.ctx, (pcm) => this.stream?.send(pcm));
    const tokenPromise = this.getAccessToken().then((accessToken) => getSttToken(accessToken, 'assemblyai'));

    let token: string;
    try {
      const [capture, tokenResponse] = await Promise.all([capturePromise, tokenPromise]);
      this.capture = capture;
      token = tokenResponse.token;
    } catch (err) {
      // Promise.all rejects as soon as either side does, without waiting for
      // (or cancelling) the other. If capture is the one still in flight and
      // it later succeeds, nothing else would ever stop that MediaStream —
      // `this.capture` was never assigned, so teardown()'s `this.capture?.stop()`
      // is a no-op. Attach cleanup directly to the promise itself so a mic
      // grant arriving after we've already given up doesn't leak.
      capturePromise.then((c) => c.stop()).catch(() => undefined);
      await this.teardown();
      this.fail(mapStartError(err));
      return;
    }
    // A stop() (unmount, tab hidden) requested while we were mid-permission-prompt
    // already put us into "finalizing" — don't clobber that by continuing to set
    // up a session nobody wants; finish the stop instead of waiting out its timer.
    if (this.stopping || this.torn) {
      void this.completeStop();
      return;
    }

    this.callbacks.onStateChange('starting', 'Connecting…');
    registerActiveSpeechSession(this.registryEntry);

    await new Promise<void>((resolveConnected) => {
      this.stream = connectAssemblyStream(token, {
        onBegin: () => {
          if (this.beginTimer) {
            clearTimeout(this.beginTimer);
            this.beginTimer = null;
          }
          // A stop() requested while still connecting must not be clobbered by a
          // late Begin flipping us back to "listening".
          if (!this.torn && !this.stopping) {
            this.callbacks.onStateChange('listening');
            addSentryBreadcrumb({
              message: '[STT] assemblyai listening',
              category: 'stt',
              data: { sampleRate: this.ctx?.sampleRate },
            });
            this.capTimer = setTimeout(() => this.finalize(true), MAX_SESSION_MS);
          }
          resolveConnected();
        },
        onTurn: (turn) => this.handleTurn(turn),
        onTermination: () => void this.completeStop(),
        onError: (err) => {
          captureException(err instanceof Error ? err : new Error('[STT] assemblyai ws error'), {
            provider: 'assemblyai',
          });
        },
        onClose: () => {
          if (this.torn) return; // our own teardown already closed it — expected
          void this.teardown().then(() => this.fail({ code: 'network-or-service', message: UNAVAILABLE_MESSAGE }));
          resolveConnected();
        },
      });

      // Never enter "listening" without a Begin — with a bad/expired token the
      // socket still opens and we'd sit there streaming audio into the void with
      // the UI stuck saying "Connecting…" forever.
      this.beginTimer = setTimeout(() => {
        void this.teardown().then(() => this.fail({ code: 'network-or-service', message: UNAVAILABLE_MESSAGE }));
        resolveConnected();
      }, BEGIN_TIMEOUT_MS);
    });
  }

  stop(options?: SpeechStopOptions): Promise<void> {
    if (this.torn) return Promise.resolve();
    return new Promise((resolve) => {
      this.resolveStop = resolve;
      this.finalize(options?.silent ?? false);
    });
  }

  private handleTurn(turn: AssemblyTurnMessage): void {
    const text = turn.transcript.trim();
    if (!turn.end_of_turn) {
      if (text) this.callbacks.onPartial(text);
      return;
    }
    if (!text) return;
    // AssemblyAI re-emits the same turn_order twice under format_turns (raw, then
    // formatted) — without this, every spoken sentence would be committed twice.
    const isRevision = this.lastTurnOrder === turn.turn_order;
    this.callbacks.onFinal(
      isRevision && this.lastCommittedText ? { text, replacesPreviousText: this.lastCommittedText } : { text },
    );
    this.lastTurnOrder = turn.turn_order;
    this.lastCommittedText = text;
  }

  private finalize(silent: boolean): void {
    if (this.torn || this.stopping) return;
    this.stopping = true;
    this.silentStop = silent;
    addSentryBreadcrumb({ message: `[STT] assemblyai finalize (silent=${silent})`, category: 'stt' });
    this.callbacks.onStateChange('finalizing');
    this.stream?.terminate();
    // The final turn(s) arrive asynchronously after Terminate — if the server
    // never answers, this still tears down instead of hanging "finalizing" forever.
    this.finalizeTimer = setTimeout(() => void this.completeStop(), FINALIZE_TIMEOUT_MS);
  }

  private async completeStop(): Promise<void> {
    if (this.torn) return;
    if (this.finalizeTimer) {
      clearTimeout(this.finalizeTimer);
      this.finalizeTimer = null;
    }
    const hadNoSpeech = this.lastCommittedText === null;
    const silent = this.silentStop;
    await this.teardown();
    if (!silent && hadNoSpeech) {
      this.fail({ code: 'silence', message: SILENCE_MESSAGE });
      return;
    }
    this.callbacks.onStateChange('idle');
    this.resolveStop?.();
    this.resolveStop = null;
  }

  private fail(error: SpeechError): void {
    this.callbacks.onError(error);
    this.callbacks.onStateChange('error');
    this.resolveStop?.();
    this.resolveStop = null;
  }

  /**
   * Idempotent teardown, ordered per the design doc: detach WS handlers before
   * closing (an onclose firing after we've already moved on would otherwise fire
   * into dead state), then stop the mic tracks (this — not closing the
   * AudioContext — is what turns off the browser's mic-in-use indicator), then
   * close the AudioContext, then clear every timer.
   */
  private async teardown(): Promise<void> {
    if (this.torn) return;
    this.torn = true;
    if (this.beginTimer) {
      clearTimeout(this.beginTimer);
      this.beginTimer = null;
    }
    if (this.finalizeTimer) {
      clearTimeout(this.finalizeTimer);
      this.finalizeTimer = null;
    }
    if (this.capTimer) {
      clearTimeout(this.capTimer);
      this.capTimer = null;
    }
    this.stream?.close();
    this.capture?.stop();
    if (this.ctx && this.ctx.state !== 'closed') {
      await this.ctx.close().catch(() => undefined);
    }
    clearActiveSpeechSession(this.registryEntry);
  }
}

function mapStartError(err: unknown): SpeechError {
  if (err instanceof CaptureError) {
    return { code: err.code, message: captureErrorMessage(err.code), cause: err };
  }
  if (err instanceof SttTokenError) {
    return { code: 'network-or-service', message: UNAVAILABLE_MESSAGE, cause: err };
  }
  return { code: 'network-or-service', message: UNAVAILABLE_MESSAGE, cause: err };
}

function captureErrorMessage(code: CaptureErrorCode): string {
  switch (code) {
    case 'permission-denied':
      return 'Microphone access was blocked.';
    case 'no-microphone':
      return 'No microphone was found on this device.';
    case 'microphone-busy':
      return 'The microphone is in use by another application.';
    default:
      return UNAVAILABLE_MESSAGE;
  }
}

export const assemblyAIProvider: SpeechProvider = {
  id: 'assemblyai',
  isSupported(): boolean {
    return isCaptureSupported();
  },
  createSession(callbacks: SpeechProviderCallbacks, context: SpeechProviderContext): SpeechSession {
    if (!context.getAccessToken) {
      throw new Error('[STT] assemblyAIProvider.createSession requires context.getAccessToken');
    }
    return new AssemblyAISession(callbacks, context.getAccessToken);
  },
};
